import * as React from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ICarImageElement } from 'data/schema';
import { analyticsState } from 'state/analytics';
import { userSession } from 'state/session';
import { getComparisonModeImages, getComparisonVehicle } from 'state/session/compare';
import { buildComparisonFilename, downloadBlob } from 'utils/ui/downloadImage';
import { buildZipFilename } from 'utils/ui/downloadImagesAsZip';
import { captureElementSnapshot } from 'utils/ui/captureElementSnapshot';
import { getMicro2DAppCaptureTarget } from 'utils/ui/getMicro2DAppCaptureTarget';
import { handleDownloadError } from 'utils/ui/handleDownloadError';
import { useCaptureComparisonImage } from 'hooks/useCaptureComparisonImage';
import { useWebComponent } from 'components/WebComponentRoot';
import { IMicro2DAppElement } from 'components/Micro2DApp';
import { DownloadMenuButton } from './DownloadMenuButton';

const COMPARISON_SHOTS = /front|side|rear|back/i;

function isDownloadableComparisonImage(image: ICarImageElement): boolean {
  return image.type === 'image' && COMPARISON_SHOTS.test(image.title);
}

export default function DownloadComparisonImageMenuItem() {
  const session = userSession().useState();
  const captureComparisonImage = useCaptureComparisonImage();
  const { rootElement } = useWebComponent();
  const [isDownloading, setIsDownloading] = React.useState(false);

  if (!userSession().canDownloadComparisonImage) {
    return false;
  }

  const activeVehicle = session.vehicles?.[session.activeVehicleId!];
  const activeVehicleCarName = session.carDisplayName || undefined;
  const [, comparingVehicle] = getComparisonVehicle();
  const comparingVehicleCarName = comparingVehicle?.carDisplayName || undefined;

  const activeImages = activeVehicle
    ? (getComparisonModeImages(activeVehicle)?.filter(isDownloadableComparisonImage) ?? [])
    : [];

  const currentAngle = activeImages[session.currentImageIndex ?? 0]?.title;

  const handleDownloadCurrent = async () => {
    try {
      const blob = await captureComparisonImage();
      const filename = buildComparisonFilename(activeVehicleCarName, comparingVehicleCarName, currentAngle);
      downloadBlob(blob, filename);
      analyticsState().setValues({ dealerToolUsed: 'downloadComparisonImage' });
    } catch {
      handleDownloadError('ERROR_COMPARISON_CAPTURE', t`Failed to capture image`);
    }
  };

  const handleDownloadAll = async () => {
    setIsDownloading(true);
    try {
      const root: Document | ShadowRoot = rootElement ?? document;
      const el = root.querySelector<IMicro2DAppElement>('micro-2d-app');
      if (!el) {
        throw new Error('micro-2d-app element not found');
      }

      // carousel can be null before Embla initialises
      if (!el.carousel) {
        throw new Error('Carousel not initialised');
      }

      // Read the freshest carousel position synchronously — the closure's
      // `session` snapshot can lag the real index by one render.
      const originalIndex = userSession().get('currentImageIndex') ?? 0;
      let currentIndex = originalIndex;

      const captured: Array<{ filename: string; data: Uint8Array }> = [];

      for (let i = 0; i < activeImages.length; i++) {
        // Embla treats scrollTo(currentIndex) as a no-op and emits no event,
        // so awaiting 'navigated-to' would stall for 2 s on the iteration that
        // targets the slide we're already on (e.g. i=0 when the dealer is on
        // the front view — the default state when entering comparison mode).
        if (i !== currentIndex) {
          // Each scrollTo fires 'navigated-to' which syncs currentImageIndex
          // to remote peers — the customer's carousel will briefly step
          // through each angle during bulk download, then snap back on
          // restore. Accepted behaviour. Filter by detail.index so a
          // concurrent peer-sync event for a different slide can't resolve
          // this iteration's promise.
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Navigation timeout')), 2000);
            const handler = (e: Event) => {
              const detail = (e as CustomEvent<{ index: number }>).detail;
              if (detail?.index !== i) return;
              el.removeEventListener('navigated-to', handler);
              clearTimeout(timeout);
              resolve();
            };
            el.addEventListener('navigated-to', handler);
            el.carousel!.scrollTo(i, true);
          });
          currentIndex = i;
        }

        // Resolve the visible capture target inside the loop in case the web
        // component re-evaluates its shadow DOM after navigation.
        const target = getMicro2DAppCaptureTarget(el);
        const blob = await captureElementSnapshot(target);
        const filename = buildComparisonFilename(activeVehicleCarName, comparingVehicleCarName, activeImages[i].title);
        captured.push({ filename, data: new Uint8Array(await blob.arrayBuffer()) });
      }

      // Restore the carousel to the slide the dealer was on before bulk
      // download. Skipped when we already ended on the original index — no
      // movement to undo, and no need to dispatch a redundant peer-sync.
      if (currentIndex !== originalIndex) {
        el.carousel.scrollTo(originalIndex, true);
      }

      const zipInput: Record<string, [Uint8Array, { level: 0 }]> = {};
      for (const { filename, data } of captured) {
        zipInput[filename] = [data, { level: 0 }];
      }
      const { zip } = await import('fflate');
      const archive = await new Promise<Uint8Array<ArrayBuffer>>((resolve, reject) => {
        zip(zipInput, { level: 0 }, (error, data) => {
          if (error) reject(error);
          else resolve(data as Uint8Array<ArrayBuffer>);
        });
      });

      const carNamePart = [activeVehicleCarName, comparingVehicleCarName].filter(Boolean).join('_vs_');
      downloadBlob(new Blob([archive], { type: 'application/zip' }), buildZipFilename(carNamePart || undefined));

      analyticsState().setValues({ dealerToolUsed: 'downloadAllComparisonImages' });
    } catch {
      handleDownloadError('ERROR_COMPARISON_DOWNLOAD_ALL', t`Failed to download images`);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <DownloadMenuButton
        onClick={handleDownloadCurrent}
        label={<Trans>Download current image</Trans>}
        testId="download-comparison-current-image"
        disabled={isDownloading}
      />
      <DownloadMenuButton
        onClick={handleDownloadAll}
        label={<Trans>Download all images</Trans>}
        testId="download-all-comparison-images"
        disabled={isDownloading}
        variant="all"
      />
    </>
  );
}
